
import { elem } from "./chrome";

export interface DeathModel {
  /** True while the player is at zero health. */
  readonly dead: boolean;
}

export class DeathScreen {
  private readonly root: HTMLElement;
  private readonly recall: HTMLButtonElement;
  private readonly here: HTMLButtonElement;

  constructor(container: HTMLElement, onRecall: () => void, onRestartHere: () => void) {
    this.root = elem(document, "div", "death");
    this.root.hidden = true;
    // Announced rather than merely drawn: this is the one screen where the
    // player is not in control and has to be told why.
    this.root.setAttribute("role", "status");
    this.root.setAttribute("aria-live", "assertive");

    // `restart_here_button`, `uiScriptLocale.RESTART_HERE` --
    // "Restart here" (`locale_en/locale/en/locale_interface.txt`). Sends
    // `/restart_here` (`uirestart.py:55-56`), which is `SCMD_RESTART_HERE`
    // (`cmd_general.cpp:635-641`): revive in place and pay the death penalty.
    this.here = elem(document, "button", "death-here");
    this.here.type = "button";
    this.here.textContent = "Restart here";
    this.here.addEventListener("click", () => onRestartHere());

    // `restart_town_button`, `uiScriptLocale.RESTART_TOWN` --
    // "Restart in the City" (same file). `/restart_town` (`uirestart.py:58-59`)
    // is `SCMD_RESTART_TOWN`, the arm that waives the experience loss.
    this.recall = elem(document, "button", "death-recall");
    this.recall.type = "button";
    this.recall.textContent = "Restart in the City";
    this.recall.addEventListener("click", () => onRecall());

    // The original's order, top to bottom: here at y=17, town at y=47.
    this.root.append(this.here, this.recall);
    container.append(this.root);
    // The legacy restart window consumes Escape without closing or changing
    // the death state (`uirestart.py:61-65`). Listen at the document because
    // the screen does not steal focus from the world; ignore detached screens
    // so test/remount lifecycles cannot leave a stale listener owning input.
    container.ownerDocument.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.root.isConnected || !this.visible) return;
      event.preventDefault();
      event.stopPropagation();
    });
  }

  update(model: DeathModel): void {
    this.root.hidden = !model.dead;
    this.recall.disabled = !model.dead;
    this.here.disabled = !model.dead;
  }

  get visible(): boolean {
    return !this.root.hidden;
  }
}
